/**
 * A sale's invoice type and its value in a foreign currency, on the screen (W3-16 (c), D-148).
 *
 * The service decides what a stated type does to a row (`gstr1.export_route` v3) and serves the
 * hold on each Raw Data row as `invoice_type_hold`; these functions only say it, and read a
 * register's cell the way the service reads it. (The column dialog that read a sheet's cells
 * before the service did went with X-14: the grid is ProDoc's mapper's now, and Raw Data offers
 * *Remember this buyer as SEZ* once the file is read.) Both tables below are held equal to the
 * service's (`app/ingestion/invoice_type.py`, exported by `pnpm gen:seam`) by
 * `seam-vocabularies.test.ts`.
 */

/** The six types, code → the words a person reads. */
export const INVOICE_TYPE_WORDS: Record<string, string> = {
  R: 'Regular',
  SEWP: 'SEZ supply with payment',
  SEWOP: 'SEZ supply without payment',
  DE: 'Deemed export',
  WPAY: 'Export with payment of IGST',
  WOPAY: 'Export without payment (under LUT)',
}

/** How a register or a person writes each, folded (`fold`) → the code. The service's own list. */
export const INVOICE_TYPE_READINGS: Record<string, string> = {
  b2b: 'R',
  de: 'DE',
  'deemed exp': 'DE',
  'deemed export': 'DE',
  'deemed exports': 'DE',
  dexp: 'DE',
  'export under lut': 'WOPAY',
  'export with payment': 'WPAY',
  'export with payment of gst': 'WPAY',
  'export with payment of igst': 'WPAY',
  'export without payment': 'WOPAY',
  'export without payment of gst': 'WOPAY',
  'export without payment of igst': 'WOPAY',
  'export without payment under lut': 'WOPAY',
  'exports with payment': 'WPAY',
  'exports without payment': 'WOPAY',
  expwop: 'WOPAY',
  expwp: 'WPAY',
  r: 'R',
  regular: 'R',
  'regular b2b': 'R',
  'regular b2b invoices': 'R',
  'regular invoice': 'R',
  'regular supply': 'R',
  sewop: 'SEWOP',
  sewp: 'SEWP',
  'sez supplies with payment': 'SEWP',
  'sez supplies with payment of igst': 'SEWP',
  'sez supplies without payment': 'SEWOP',
  'sez supplies without payment of igst': 'SEWOP',
  'sez supply with payment': 'SEWP',
  'sez supply with payment of igst': 'SEWP',
  'sez supply without payment': 'SEWOP',
  'sez supply without payment of igst': 'SEWOP',
  'sez with payment': 'SEWP',
  'sez without payment': 'SEWOP',
  sezwop: 'SEWOP',
  sezwp: 'SEWP',
  'with payment of gst': 'WPAY',
  'with payment of igst': 'WPAY',
  'without payment of gst': 'WOPAY',
  'without payment of igst': 'WOPAY',
  wopay: 'WOPAY',
  wpay: 'WPAY',
}

/** A cell reduced to lower-case words, as the service folds it. */
export function fold(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** The code a cell or a typed value names, or null when it names none of the six. */
export function invoiceTypeOf(value: unknown): string | null {
  return INVOICE_TYPE_READINGS[fold(value)] ?? null
}

/** A stored type in words; one the service could not read, as it was written. */
export function invoiceTypeWords(value: unknown): string | null {
  if (value === null || value === undefined || String(value).trim() === '') return null
  return INVOICE_TYPE_WORDS[String(value)] ?? String(value)
}

/**
 * The value in the invoice's own currency, beside the code: `USD 1234.50`. Never formatted as
 * rupees — it is not rupees, and nothing here adds it to anything.
 */
export function foreignValueWords(currency: unknown, fcValue: unknown): string | null {
  const value = fcValue === null || fcValue === undefined ? '' : String(fcValue).trim()
  if (!value) return null
  const code = currency === null || currency === undefined ? '' : String(currency).trim()
  return code ? `${code} ${value}` : `${value} (currency not stated)`
}
